import React, {useMemo} from "react";
import {Card, Input, Tag, Empty, Tooltip, Select} from "antd";
import {ClockCircleOutlined} from "@ant-design/icons";
import {
    courierBonusCampaignStatuses,
    courierBonusCampaignTypes,
    daysList,
    guaranteeCalculationFactors
} from "../../../../../constants/constants";
import {
    CampaignsContainer,
    CampaignCard,
    CampaignHeader,
    CampaignTitle,
    CampaignBody,
    FormInputRow,
    FormItem,
    FormLabel,
    CompactFormItem,
    CompactLabel,
    PackageBonusList,
    PackageBonusItem,
    TimeSection,
    TimeSectionTitle,
    CampaignItemsList,
    CampaignItem,
    CampaignItemHeader,
    CampaignItemName,
    CampaignItemBody
} from "./subCompanyPoolCampaigns.styles";
import moment from "moment";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";

const getCampaignTypeLabel = campaignType => {
    return (
        Object.values(courierBonusCampaignTypes).find(type => type.value === campaignType)?.label ||
        ""
    );
};

const getFactorLabel = factorValue => {
    return (
        Object.values(guaranteeCalculationFactors).find(f => f.value === factorValue)?.label ||
        factorValue
    );
};

const formatDate = dateString => {
    if (!dateString) return "";
    return moment(dateString).format("DD.MM.YYYY HH:mm");
};

const getSortedDays = days => {
    if (!days || days.length === 0) return [];
    return [...days].sort((a, b) => a - b);
};

// Tek bir kampanya öğesi için içerik renderer'ları
const CampaignContentRenderer = ({campaign}) => {
    const campaignType = campaign?.type;
    const conditions = campaign?.conditions || {};

    const renderHourlyFeeContent = () => {
        // Saatlik kazanç için kompakt gösterim TimeAndDaysSection'da yapılacak
        return null;
    };

    const renderPackageBonusFeeContent = () => {
        const packageBonusFee = conditions.packageBonusFee || {};
        const courierAmounts = packageBonusFee.courierAmountsByPackageCount || [];

        if (courierAmounts.length === 0) {
            return <Empty description="Paket bonus tanımı bulunamadı" />;
        }

        return (
            <PackageBonusList>
                {courierAmounts.map((item, index) => (
                    <PackageBonusItem key={item._id || index}>
                        <CompactFormItem>
                            <CompactLabel>Min. Paket:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={item.minimumPackageCount || ""}
                                disabled
                                style={{width: 70}}
                            />
                        </CompactFormItem>
                        <CompactFormItem>
                            <CompactLabel>Kurye Tutarı:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={item.courierAmount || ""}
                                disabled
                                style={{width: 80}}
                            />
                        </CompactFormItem>
                    </PackageBonusItem>
                ))}
            </PackageBonusList>
        );
    };

    const renderHourlyGuaranteeFeeContent = () => {
        // Saatlik garanti için kompakt gösterim TimeAndDaysSection'da yapılacak
        return null;
    };

    switch (campaignType) {
        case courierBonusCampaignTypes.HOURLY_FEE.value:
            return renderHourlyFeeContent();
        case courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value:
            return renderPackageBonusFeeContent();
        case courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value:
            return renderHourlyGuaranteeFeeContent();
        default:
            return null;
    }
};

// Zaman ve gün bilgileri
const TimeAndDaysSection = ({campaign, compact = false}) => {
    const sortedDays = getSortedDays(campaign?.days);
    const conditions = campaign?.conditions || {};
    const hourlyFee = conditions.hourlyFee || {};
    const hourlyGuaranteeFee = conditions.hourlyGuaranteeFee || {};
    const calculationFactors = hourlyGuaranteeFee.calculationFactors || [];
    const isHourlyFee = campaign?.type === courierBonusCampaignTypes.HOURLY_FEE.value;
    const isPackageBonusFee = campaign?.type === courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value;
    const isHourlyGuarantee =
        campaign?.type === courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value;

    // Tüm kampanya tipleri için kompakt tek satır gösterim
    if (compact || isHourlyFee || isPackageBonusFee || isHourlyGuarantee) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                    padding: "6px 0"
                }}
            >
                {isHourlyFee && (
                    <>
                        <CompactFormItem>
                            <CompactLabel>Firma Tutarı:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={hourlyFee.companyAmount || ""}
                                disabled
                                style={{width: 70}}
                            />
                        </CompactFormItem>
                        <CompactFormItem>
                            <CompactLabel>Kurye Tutarı:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={hourlyFee.courierAmount || ""}
                                disabled
                                style={{width: 70}}
                            />
                        </CompactFormItem>
                    </>
                )}
                {isHourlyGuarantee && (
                    <>
                        <CompactFormItem>
                            <CompactLabel>Kurye Garanti Tutarı:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={hourlyGuaranteeFee.courierGuaranteeAmount || ""}
                                disabled
                                style={{width: 70}}
                            />
                        </CompactFormItem>
                        <CompactFormItem>
                            <CompactLabel>Müşteri Garanti Tutarı:</CompactLabel>
                            <Input
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={hourlyGuaranteeFee.subCompanyGuaranteeAmount || ""}
                                disabled
                                style={{width: 70}}
                            />
                        </CompactFormItem>
                        {calculationFactors?.length > 0 && (
                            <CompactFormItem style={{maxWidth: "100%"}}>
                                <CompactLabel>Hesaplama Faktörleri:</CompactLabel>
                                <Select
                                    className="payment-contract-form-input"
                                    mode="multiple"
                                    size="small"
                                    value={calculationFactors.map(f => getFactorLabel(f))}
                                    disabled
                                    style={{minWidth: 150, maxWidth: 250}}
                                    dropdownStyle={{display: "none"}}
                                />
                            </CompactFormItem>
                        )}
                    </>
                )}
                <CompactFormItem>
                    <CompactLabel>Başlangıç Zamanı:</CompactLabel>
                    <Tooltip title={campaign?.startHour || "-"}>
                        <Input
                            className="payment-contract-form-input"
                            size="small"
                            value={campaign?.startHour || "-"}
                            disabled
                            style={{width: 80}}
                            suffix={<ClockCircleOutlined style={{color: "#bfbfbf"}} />}
                        />
                    </Tooltip>
                </CompactFormItem>
                <CompactFormItem>
                    <CompactLabel>Bitiş Zamanı:</CompactLabel>
                    <Tooltip title={campaign?.endHour || "-"}>
                        <Input
                            className="payment-contract-form-input"
                            size="small"
                            value={campaign?.endHour || "-"}
                            disabled
                            style={{width: 80}}
                            suffix={<ClockCircleOutlined style={{color: "#bfbfbf"}} />}
                        />
                    </Tooltip>
                </CompactFormItem>
                <CompactFormItem style={{maxWidth: "100%"}}>
                    <CompactLabel>Günler:</CompactLabel>
                    <Select
                        className="payment-contract-form-input"
                        mode="multiple"
                        size="small"
                        value={sortedDays.map(day => {
                            const dayInfo = daysList.find(d => d.value === day);
                            return dayInfo?.name?.substring(0, 3) || day;
                        })}
                        disabled
                        style={{minWidth: 100}}
                        dropdownStyle={{display: "none"}}
                    />
                </CompactFormItem>
            </div>
        );
    }
    return null;
};

// Tip bazlı kampanya kartı (aynı tipteki kampanyaları gruplar)
const CampaignTypeCard = ({campaignType, campaigns}) => {
    return (
        <CampaignCard>
            <CampaignHeader>
                <CampaignTitle>{getCampaignTypeLabel(campaignType)}</CampaignTitle>
                <Tag>{campaigns.length} kampanya</Tag>
            </CampaignHeader>
            <CampaignBody>
                <CampaignItemsList>
                    {campaigns.map(campaign => (
                        <CampaignItem key={campaign._id}>
                            <CampaignItemHeader>
                                <CampaignItemName>{campaign.name}</CampaignItemName>
                                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                    <span style={{fontSize: 12, color: "#666"}}>
                                        <Tooltip title={"Kampanya Başlangıç Tarihi"}>
                                            {formatDate(campaign?.startDate)}
                                        </Tooltip>{" "}
                                        -{" "}
                                        <Tooltip title={"Kampanya Bitiş Tarihi"}>
                                            {campaign?.endDate
                                                ? formatDate(campaign?.endDate)
                                                : "Bitiş Tarihi Seçilmedi"}
                                        </Tooltip>
                                    </span>
                                </div>
                            </CampaignItemHeader>
                            <CampaignItemBody>
                                <CampaignContentRenderer campaign={campaign} />
                                <TimeAndDaysSection campaign={campaign} />
                            </CampaignItemBody>
                        </CampaignItem>
                    ))}
                </CampaignItemsList>
            </CampaignBody>
        </CampaignCard>
    );
};

export const SubCompanyPoolCampaigns = ({subCompanyId, campaigns = []}) => {
    const stores = useStore();
    const [_, subcompanyPool] = useFetch({
        fetchFn: stores.poolStore.getPoolBySubCompanyId.bind(null, {
            subCompanyId: subCompanyId
        }),
        dependencies: [subCompanyId],
        initialCall: !!subCompanyId
    });

    // Kampanyaları tipe göre grupla
    const groupedCampaigns = useMemo(() => {
        const groups = {};

        // Tip sıralaması için sabit sıra
        const typeOrder = [
            courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value,
            courierBonusCampaignTypes.HOURLY_FEE.value,
            courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value
        ];

        campaigns.forEach(campaign => {
            const type = campaign.type;
            if (type === courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value) {
                const hourlyGuaranteeFee = campaign?.conditions?.hourlyGuaranteeFee;
                if (!hourlyGuaranteeFee?.subCompanyId || hourlyGuaranteeFee.subCompanyId !== subCompanyId) {
                    return;
                }
            }
            if (!groups[type]) {
                groups[type] = [];
            }
            groups[type].push(campaign);
        });

        // Sıralı array olarak döndür
        return typeOrder
            .filter(type => groups[type] && groups[type].length > 0)
            .map(type => ({
                type,
                campaigns: groups[type]
            }));
    }, [campaigns]);

    return (
        <Card
            size="small"
            style={{
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "0px 4px 16px 4px",
                border: "1px solid #f0f0f0"
            }}
        >
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <h3 style={{fontWeight: 700, fontSize: 20, margin: "0px 0px 10px 0px"}}>
                    Havuz Kampanyaları
                </h3>
                {subcompanyPool?.name && (
                    <Tag style={{fontSize: 12, fontWeight: 300}}>{subcompanyPool.name}</Tag>
                )}
            </div>
            {!campaigns || campaigns.length === 0 ? (
                <Empty description="Havuza tanımlı aktif bonus kampanyası bulunmuyor" />
            ) : (
                <CampaignsContainer>
                    {groupedCampaigns.map(({type, campaigns: typeCampaigns}) => (
                        <CampaignTypeCard
                            key={type}
                            campaignType={type}
                            campaigns={typeCampaigns}
                        />
                    ))}
                </CampaignsContainer>
            )}
        </Card>
    );
};
